import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { useNotification } from '../../../../../hooks'
import { getRecipientRealId } from '../../../../../utils/email'
import { expandRecipientEmailsForTo } from '../../../EmailRecipientsField'
import { EMAIL_CREATION_FORM_FIELD_NAME_TO } from '../../EmailCreationForm.constants'
import { setEmailCreationFormAutoAddAdditionalContactsStorage } from '../../EmailCreationForm.utils'

import {
  fetchAndExpand,
  getInitialEnabled,
} from './useEmailRecipientsAutoAdd.utils'

export function useEmailRecipientsAutoAdd({ disabled = false } = EMPTY_OBJECT) {
  const [enabled, setEnabledState] = useState(() => getInitialEnabled(disabled))

  const { setFieldValue, values } = useFormikContext() || EMPTY_OBJECT

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { t } = useTranslation()

  function showNoAdditionalEmailsNotification() {
    handleAddSuccessNotification(t('Email.NoAdditionalEmailsFoundKey'))
  }

  // When auto-add is on, dropdown search runs with the expand param so each
  // selected option already carries the full `emails` array. We expand
  // synchronously from that data — no per-select fetch required. The
  // toggle-on path (expandExistingTo) is the single place that fetches.
  function expandSelectedRecipientEmails(currentTo, selectedOption) {
    if (!enabled) {
      return null
    }

    const additions = expandRecipientEmailsForTo(currentTo, selectedOption)

    if (additions.length === 0) {
      showNoAdditionalEmailsNotification()
    }

    return additions
  }

  async function expandExistingTo() {
    if (disabled) {
      return
    }

    const currentTo = values?.[EMAIL_CREATION_FORM_FIELD_NAME_TO] || EMPTY_LIST

    if (currentTo.length > 0) {
      // Pass 1: recipients that already carry expanded email data can be
      // expanded immediately. Recipients without it have their realIds queued
      // for the pass 2 fetch.
      const initialState = currentTo.reduce(
        (acc, item) => {
          const realId = getRecipientRealId(item)

          if (realId === null) {
            return acc
          }

          if (Array.isArray(item.emails) && item.emails.length > 1) {
            const additions = expandRecipientEmailsForTo(acc.workingTo, item)

            return {
              ...acc,
              hasAdditional: true,
              workingTo:
                additions.length > 0
                  ? [...acc.workingTo, ...additions]
                  : acc.workingTo,
            }
          }

          return { ...acc, idsNeedingFetch: [...acc.idsNeedingFetch, realId] }
        },
        { hasAdditional: false, idsNeedingFetch: [], workingTo: currentTo }
      )

      // Pass 2: fetch expanded email data for recipients selected while the
      // switch was off (their stored emails field is absent or incomplete).
      const finalState =
        initialState.idsNeedingFetch.length > 0
          ? await fetchAndExpand(initialState)
          : initialState

      if (finalState.workingTo !== currentTo) {
        setFieldValue?.(EMAIL_CREATION_FORM_FIELD_NAME_TO, finalState.workingTo)
      }

      if (!finalState.hasAdditional) {
        showNoAdditionalEmailsNotification()
      }
    }
  }

  function setEnabled(nextEnabled) {
    if (disabled) {
      return
    }

    setEnabledState(nextEnabled)
    setEmailCreationFormAutoAddAdditionalContactsStorage(String(nextEnabled))

    if (nextEnabled) {
      expandExistingTo().catch(() => {
        handleAddErrorNotification(t('Error.CommonApiRequestFailKey'))
      })
    }
  }

  return {
    enabled: !disabled && enabled,
    expandSelectedRecipientEmails,
    setEnabled,
  }
}
